import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  alpha,
  Box,
  ButtonBase,
  Card,
  Chip,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Check, ChevronRight, Upload } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Button, Table } from "../ds";
import type { DataTableColumn } from "../ds/Table";
import ImportSuggestionsDialog from "../components/ImportSuggestionsDialog";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { useRegisterTopBarActions } from "../context/TopBarActionsContext";
import StatusChip from "../components/StatusChip";
import SuggestionFilters from "../components/SuggestionFilters";
import { getSuggestionStats, listSuggestions } from "../api/ess";
import { displayDepartment } from "../api/departments";
import { useDepartments } from "../hooks/useDepartments";
import { statusCategory, useWorkflowConfig } from "../hooks/useWorkflow";
import { formatDateTime } from "../utils/format";
import { slaState } from "../utils/sla";
import { unitGroupLabel } from "../utils/departments";
import {
  displayCategory,
  resolveSuggestionUnit,
  suggestionEvaluations,
} from "../types/ess";
import type {
  Suggestion,
  SuggestionListFilters,
  SuggestionStatus,
} from "../types/ess";

function parseFiltersFromSearch(
  sp: URLSearchParams,
): {
  filters: SuggestionListFilters;
  mine: boolean;
  from: string | null;
  label: string | null;
} {
  const splitCsv = (key: string) =>
    sp.get(key)?.split(",").filter(Boolean) ?? undefined;
  return {
    filters: {
      status: splitCsv("status") as SuggestionStatus[] | undefined,
      category: splitCsv("category"),
      department: splitCsv("department"),
      unit: splitCsv("unit"),
      reward_category: splitCsv("reward_category"),
      date_from: sp.get("date_from") ?? undefined,
      date_to: sp.get("date_to") ?? undefined,
    },
    mine: sp.get("mine") === "1",
    // Drill-down marker set by Overview's tile links. Preserved across
    // filter edits so the back-arrow affordance stays stable while users
    // fine-tune the filtered view.
    from: sp.get("from"),
    // Source-affordance label that the user clicked on the Overview
    // tile ("Passed SC", "Department · PP"). AppLayout reads this back
    // as the AppBar title. Sticky alongside `from` so edits don't lose it.
    label: sp.get("label"),
  };
}

// Pill-style toggle. Label sits to the left of a soft-tinted track with a
// white circular thumb that slides + carries a check icon when active.
// Used in place of the DS Switch on the Suggestions filter row to give
// "Only mine" stronger visual weight than a default Switch.
function PillToggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  const theme = useTheme();
  const accent = theme.palette.primary.main;
  return (
    <ButtonBase
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1.25,
        borderRadius: 999,
        px: 0.25,
        py: 0.25,
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 14,
          fontWeight: 600,
          color: "text.primary",
          letterSpacing: 0,
          textTransform: "none",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          position: "relative",
          width: 44,
          height: 24,
          borderRadius: 999,
          bgcolor: checked
            ? alpha(accent, 0.18)
            : alpha(theme.palette.text.primary, 0.1),
          transition:
            "background-color .18s ease, box-shadow .18s ease",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 2,
            left: checked ? 22 : 2,
            width: 20,
            height: 20,
            borderRadius: "50%",
            bgcolor: "background.paper",
            display: "grid",
            placeItems: "center",
            transition: "left .22s cubic-bezier(.4,0,.2,1)",
            boxShadow:
              `0 1px 2px rgba(0,0,0,0.18), 0 0 0 1px ${alpha(
                theme.palette.text.primary,
                0.06,
              )}`,
          }}
        >
          {checked && (
            <RavenIcon icon={Check} sx={{ fontSize: 13, color: accent }} />
          )}
        </Box>
      </Box>
    </ButtonBase>
  );
}

export default function Landing() {
  const { isAdmin } = useIsAdmin();
  const [importOpen, setImportOpen] = useState(false);
  const [importedIds, setImportedIds] = useState<string[]>([]);
  // Register page-owned actions in the shell so Import sits beside New suggestion.
  const importAction = useMemo(
    () => isAdmin ? (
      <Button
        variant="outlined"
        aria-label="Import suggestions"
        startIcon={<RavenIcon icon={Upload} />}
        onClick={() => setImportOpen(true)}
        sx={{
          minWidth: { xs: 40, sm: "auto" },
          px: { xs: 1, sm: 2 },
          "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: 0 },
        }}
      >
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          Import
        </Box>
      </Button>
    ) : null,
    [isAdmin],
  );
  useRegisterTopBarActions(importAction);
  const navigate = useNavigate();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  // Short viewports (landscape mobile, low-res laptops): the
  // autofit-to-viewport behavior leaves no room for rows. Use this
  // alongside `isMobile` to fall back to a static rowsPerPage and let
  // the page scroll naturally.
  const isShortViewport = useMediaQuery("(max-height: 600px)");
  const useFlowLayout = isMobile || isShortViewport;
  const [searchParams, setSearchParams] = useSearchParams();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => parseFiltersFromSearch(searchParams), []);
  const [filters, setFilters] = useState<SuggestionListFilters>(initial.filters);
  const [mine, setMine] = useState(initial.mine);
  // Drill-down markers (`from`, `label`) come from the URL on each
  // render rather than from local state. That way, when the user
  // clicks the Suggestions sidebar entry — which navigates to a
  // clean /suggestions — the URL is genuinely cleared instead of
  // being re-stamped with stale markers from a useState snapshot.
  // The trade-off vs the old "sticky state" approach: edits to the
  // filter row keep the markers because they were already in the URL,
  // a fresh nav drops them naturally.
  const fromParam = searchParams.get("from");
  const labelParam = searchParams.get("label");
  const facetParam = searchParams.get("facet");
  const reachedParam = searchParams.get("reached");
  // "SLA breached" facet — same single `facet` URL slot as Reports' Aging
  // tile drill-down (`facet=aging`), but toggleable directly from here
  // (Aging is a one-way marker set only by Reports, so there's nothing to
  // mirror on the "clicking it in Landing" side — this is the first
  // facet with its own in-page pill).
  const isSlaBreachedFacet = facetParam === "sla_breached";
  const setSlaBreachedFacet = (next: boolean) => {
    const sp = new URLSearchParams(searchParams);
    if (next) {
      sp.set("facet", "sla_breached");
    } else if (facetParam === "sla_breached") {
      sp.delete("facet");
    }
    setSearchParams(sp, { replace: true });
  };

  useEffect(() => {
    const next = new URLSearchParams();
    const add = (key: string, v: string | string[] | undefined) => {
      if (Array.isArray(v)) {
        if (v.length) next.set(key, v.join(","));
      } else if (v) {
        next.set(key, v);
      }
    };
    add("status", filters.status);
    add("category", filters.category);
    add("department", filters.department);
    add("unit", filters.unit);
    add("reward_category", filters.reward_category);
    add("date_from", filters.date_from);
    add("date_to", filters.date_to);
    if (mine) next.set("mine", "1");
    if (fromParam) next.set("from", fromParam);
    if (labelParam) next.set("label", labelParam);
    if (facetParam) next.set("facet", facetParam);
    if (reachedParam) next.set("reached", reachedParam);
    setSearchParams(next, { replace: true });
  }, [
    filters,
    mine,
    fromParam,
    labelParam,
    facetParam,
    reachedParam,
    setSearchParams,
  ]);

  // External-nav reset: when the URL gets stripped of every filter param
  // — typically because the user clicked the Suggestions sidebar entry
  // while already on the filtered list — sync the local state down so
  // the table actually unfilters. Without this, the URL-emit effect
  // above would re-stamp the stale state right back into the URL.
  const urlIsClean =
    !searchParams.get("status") &&
    !searchParams.get("category") &&
    !searchParams.get("department") &&
    !searchParams.get("unit") &&
    !searchParams.get("reward_category") &&
    !searchParams.get("date_from") &&
    !searchParams.get("date_to") &&
    !searchParams.get("mine") &&
    !searchParams.get("from");
  useEffect(() => {
    if (urlIsClean) {
      setFilters({});
      setMine(false);
    }
  }, [urlIsClean]);

  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  // Auto-fit pagination. The page wrapper is flex-column-fill-viewport,
  // the Card itself is flex:1 (consumes remaining space below the filter
  // row) — so the *Card's actual rendered height* is the source of truth,
  // not a stack of viewport-minus-chrome estimates that drift as the
  // surrounding chrome changes. ResizeObserver keeps the row count in
  // sync with whatever the Card is currently sized to.
  //
  // User-driven changes via the page-size dropdown set
  // `manualRowsPerPage`, which freezes the auto-fit (so resizing the
  // window after a manual pick doesn't surprise-override the choice).
  const tableCardRef = useRef<HTMLDivElement | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [manualRowsPerPage, setManualRowsPerPage] = useState(false);

  useEffect(() => {
    if (manualRowsPerPage) return;
    // Flow layout (mobile, or short landscape viewports): skip the
    // card-height-based autofit. The table is allowed to overflow the
    // viewport vertically and the page scrolls naturally; otherwise a
    // 430px-tall landscape phone shows a table chrome with zero rows.
    if (useFlowLayout) {
      setRowsPerPage((prev) => (prev === 10 ? prev : 10));
      return;
    }
    const ROW_PX = 48; // Table td height
    const HEADER_PX = 48; // table head
    const FOOTER_PX = 56; // pagination footer
    const compute = () => {
      const card = tableCardRef.current;
      if (!card) return;
      const cardHeight = card.getBoundingClientRect().height;
      const rowsArea = cardHeight - HEADER_PX - FOOTER_PX;
      const fit = Math.max(5, Math.floor(rowsArea / ROW_PX));
      setRowsPerPage((prev) => (prev === fit ? prev : fit));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (tableCardRef.current) ro.observe(tableCardRef.current);
    return () => ro.disconnect();
  }, [manualRowsPerPage, useFlowLayout]);

  const statsDepartment =
    filters.department && filters.department.length === 1
      ? filters.department[0]
      : undefined;
  const statsQuery = useQuery({
    queryKey: [
      "ess-stats",
      filters.date_from,
      filters.date_to,
      statsDepartment,
    ],
    queryFn: () =>
      getSuggestionStats({
        date_from: filters.date_from,
        date_to: filters.date_to,
        department: statsDepartment,
      }),
  });

  // Unit isn't stored as a column on the suggestion — it's a property of
  // its department (with `metadata.unit` stamped on newer rows). Map
  // department name -> unit group once so each row / the Unit filter can
  // resolve without a per-row lookup. Uses `unitGroupLabel` (not raw
  // `d.unit`) so common/unit-less departments resolve to "Common" — the
  // same grouping the Department dropdown and the Unit filter's option
  // list use.
  const { data: departmentList } = useDepartments();
  const unitByDepartment = useMemo(() => {
    const m: Record<string, string> = {};
    (departmentList ?? []).forEach((d) => {
      if (d?.name) m[d.name] = unitGroupLabel(d);
    });
    return m;
  }, [departmentList]);

  const listQuery = useQuery({
    // unitByDepartment participates in the key: the Unit filter is applied
    // inside listSuggestions using this lookup, so a late-arriving
    // department catalog must re-run the post-filter.
    queryKey: ["ess-list", filters, mine, unitByDepartment],
    queryFn: () =>
      listSuggestions(
        { ...filters, my_suggestions: mine || undefined },
        unitByDepartment,
      ),
  });

  const departments = useMemo(
    () => Object.keys(statsQuery.data?.by_department ?? {}).sort(),
    [statsQuery.data],
  );

  // Legacy suggestions may carry a category from before the current
  // CATEGORIES list — source options from the un-filtered stats endpoint
  // (same as departments above) so the Category filter can still select
  // them even when the active Status/Department/Period filters would
  // otherwise hide every row in that category.
  const categories = useMemo(
    () => Object.keys(statsQuery.data?.by_category ?? {}).sort(),
    [statsQuery.data],
  );

  // Workflow config drives `slaState`'s categoryOf (terminal vs open) for
  // the "SLA breached" facet predicate below.
  const { data: workflow } = useWorkflowConfig();

  const columns: DataTableColumn<Suggestion>[] = isMobile
    ? [
        {
          field: "title",
          header: "Suggestion",
          render: (r) => (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="span"
                sx={{
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: "primary.main",
                  letterSpacing: "0.04em",
                  display: "block",
                  lineHeight: 1.3,
                }}
                noWrap
              >
                {r.suggestion_number ?? "—"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: "text.primary",
                }}
                noWrap
              >
                {r.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                  display: "block",
                  lineHeight: 1.4,
                }}
                noWrap
              >
                {r.employee_name}
              </Typography>
            </Box>
          ),
        },
        {
          field: "status",
          header: "",
          width: 112,
          align: "right",
          render: (r) => <StatusChip status={r.status} short />,
        },
        {
          field: "_action" as keyof Suggestion,
          header: "",
          width: 36,
          align: "center",
          render: (r) => (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/suggestions/${r.id}`);
              }}
              aria-label={`Open suggestion ${r.suggestion_number}`}
              sx={{ color: "text.secondary" }}
            >
              <RavenIcon icon={ChevronRight} sx={{ fontSize: 12 }} />
            </IconButton>
          ),
        },
      ]
    : [
        {
          field: "suggestion_number",
          header: "No.",
          width: 112,
          render: (r) => (
            <Typography
              variant="body2"
              noWrap
              sx={{ color: "primary.main", fontWeight: 600 }}
            >
              {r.suggestion_number ?? "—"}
            </Typography>
          ),
        },
        { field: "title", header: "Title", width: 240, tooltip: true },
        {
          field: "employee_name",
          header: "Employee",
          width: 130,
        },
        ...(isCompact
          ? []
          : [
              {
                field: "department" as keyof Suggestion,
                header: "Department",
                width: 108,
                render: (row: Suggestion) => displayDepartment(row.department),
              },
              {
                field: "_unit" as keyof Suggestion,
                header: "Unit",
                width: 72,
                render: (row: Suggestion) =>
                  resolveSuggestionUnit(row, unitByDepartment) ?? "—",
              },
              {
                field: "category" as keyof Suggestion,
                header: "Category",
                width: 132,
                render: (row: Suggestion) => displayCategory(row),
              },
              {
                field: "_reward" as keyof Suggestion,
                header: "Reward",
                width: 60,
                align: "center" as const,
                render: (row: Suggestion) =>
                  suggestionEvaluations(row).ssc?.reward_category ?? "—",
              },
            ]),
        {
          field: "status",
          header: "Status",
          width: 148,
          render: (r) => <StatusChip status={r.status} short />,
        },
        {
          field: "created_at",
          header: "Submitted",
          width: 140,
          render: (r) => (
            <Typography variant="body2" noWrap color="text.secondary">
              {formatDateTime(r.created_at)}
            </Typography>
          ),
        },
        {
          field: "_action" as keyof Suggestion,
          header: "",
          width: 44,
          align: "center",
          render: (r) => (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/suggestions/${r.id}`);
              }}
              aria-label={`Open suggestion ${r.suggestion_number}`}
              sx={{ color: "text.secondary" }}
            >
              <RavenIcon icon={ChevronRight} sx={{ fontSize: 14 }} />
            </IconButton>
          ),
        },
      ];

  const allRowsRaw = useMemo(
    () => listQuery.data?.suggestions ?? [],
    [listQuery.data],
  );
  // Client-side filtering layered on top of the server-side filtered
  // list (status / department / category / date_from / date_to are
  // already applied upstream). Three extra predicates land here:
  //   1. searchQuery — instant full-text across title / number / employee
  //   2. facet=aging — `updated_at` more than 14 days ago, matches the
  //      Aging tile's count math. Backend can't express "updated_before"
  //      yet, so it lives client-side until that's added.
  //   3. facet=sla_breached — `slaState(row, categoryOf).breached`, i.e.
  //      past the 90-day SLA window (open-and-late OR closed/rejected
  //      late) — same reason it's client-side: no backend predicate for it.
  const allRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const isAgingFacet = facetParam === "aging";
    const agingCutoff = isAgingFacet
      ? Date.now() - 14 * 86400000
      : null;
    const categoryOf = (status: string) => statusCategory(workflow, status);
    return allRowsRaw.filter((r) => {
      if (q) {
        const haystack = [
          r.title,
          r.suggestion_number,
          r.employee_name,
          r.employee_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (
        agingCutoff !== null &&
        new Date(r.updated_at).getTime() >= agingCutoff
      ) {
        return false;
      }
      if (isSlaBreachedFacet && !slaState(r, categoryOf).breached) {
        return false;
      }
      return true;
    }).sort((a, b) => Number(importedIds.includes(b.id)) - Number(importedIds.includes(a.id)));
  }, [allRowsRaw, searchQuery, facetParam, isSlaBreachedFacet, workflow, importedIds]);
  const paged = allRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    // Outer fills the AppLayout content area top-to-bottom. The negative
    // margin neutralizes the parent <Box>'s `p: { xs: 2, md: 3 }` so we
    // can re-pad here while still hugging the viewport edges precisely.
    // calc accounts for the AppBar Toolbar spacer (64px) and the parent
    // wrapper's vertical padding (32px md / 16px xs each side).
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // Toolbar is 56px on xs, 64px on sm+. Wrapper padding is 16px on
        // xs (`p: 2`) and 24px on md+ (`p: 3`) — doubled (top+bottom) is
        // the second subtraction.
        // Flow layout (narrow or short viewports): page grows
        // naturally and scrolls. Otherwise lock to viewport so the
        // pagination footer pins to the card's bottom edge.
        height: useFlowLayout ? "auto" : "calc(100vh - 64px - 48px)",
        minHeight: 0,
      }}
    >
      <ImportSuggestionsDialog open={importOpen} onClose={() => setImportOpen(false)} onImported={ids => { setImportedIds(ids); setPage(0); }} />
      {importedIds.length > 0 && <Box mb={1}><Chip label={`${importedIds.length} newly imported · current filters may hide some rows`} onDelete={() => setImportedIds([])} /></Box>}
      <Box mb={2}>
        <SuggestionFilters
          value={filters}
          onChange={(f) => {
            setFilters(f);
            setPage(0);
          }}
          departments={departments}
          categories={categories}
          disabled={fromParam === "overview"}
          statusSummary={
            reachedParam
              ? {
                  label: `Reached: ${reachedParam}`,
                  // Leaving the drill-down: a clean URL resets the local
                  // filters (the external-nav reset below).
                  onClear: () => navigate("/suggestions"),
                }
              : undefined
          }
          search={searchQuery}
          onSearchChange={(q) => {
            setSearchQuery(q);
            setPage(0);
          }}
          trailing={
            <Stack
              direction="row"
              spacing={{ xs: 1, sm: 2 }}
              useFlexGap
              flexWrap="wrap"
              alignItems="center"
              data-tour="suggestions-toggles"
            >
              <PillToggle
                label="Only mine"
                checked={mine}
                onChange={(v) => setMine(v)}
                disabled={fromParam === "overview"}
              />
              <PillToggle
                label="SLA breached"
                checked={isSlaBreachedFacet}
                onChange={setSlaBreachedFacet}
                disabled={fromParam === "overview"}
              />
            </Stack>
          }
        />
        {/* Persistent scope bar — present even when no filter is active
          so the user always knows what they're looking at. Period chip
          omitted on this page; the Period dropdown above already
          surfaces the active range. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing{" "}
            <Box
              component="span"
              sx={{ color: "text.primary", fontWeight: 600 }}
            >
              {allRows.length.toLocaleString()}{" "}
              {allRows.length === 1 ? "suggestion" : "suggestions"}
            </Box>
          </Typography>
          {mine && (
            <Chip
              label="Only Mine"
              size="small"
              onDelete={() => setMine(false)}
              sx={(theme) => ({
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                "& .MuiChip-deleteIcon": {
                  color: theme.palette.text.secondary,
                  "&:hover": { color: theme.palette.text.primary },
                },
              })}
            />
          )}
          {isSlaBreachedFacet && (
            <Chip
              label="SLA Breached"
              size="small"
              onDelete={() => setSlaBreachedFacet(false)}
              sx={(theme) => ({
                bgcolor: alpha(theme.palette.error.main, 0.08),
                // error.dark is a light-page ink; the dark palette's is a
                // deep red that sinks into the page.
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.error.main
                    : theme.palette.error.dark,
                "& .MuiChip-deleteIcon": {
                  color: theme.palette.error.main,
                  "&:hover": { color: theme.palette.error.dark },
                },
              })}
            />
          )}
        </Stack>
      </Box>
      <Card
        ref={tableCardRef}
        data-tour="suggestions-table"
        sx={{
          overflow: "hidden",
          // Flow layout: size to content (page scrolls). Otherwise
          // grow to fill remaining card area so pagination pins to
          // the bottom of the viewport.
          flex: useFlowLayout ? "0 0 auto" : 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Table<Suggestion>
          columns={columns}
          rows={paged}
          rowKey="id"
          isRowHighlighted={row => importedIds.includes(row.id)}
          page={page}
          rowsPerPage={rowsPerPage}
          total={allRows.length}
          onPageChange={setPage}
          onRowsPerPageChange={(rpp) => {
            setManualRowsPerPage(true);
            setRowsPerPage(rpp);
            setPage(0);
          }}
          onRowClick={(row) => navigate(`/suggestions/${row.id}`)}
          loading={listQuery.isLoading}
          emptyLabel="No suggestions match these filters"
          noBorder
        />
      </Card>
    </Box>
  );
}
